/**
 * FLUID = Flexible, Lightweight, Universal, Interactive, and Dynamic
 * FLUID = Flux Universal Identifier for Data
 */
export class FluidBuilder<T extends string = string> {
  public static FLUID_PREFIX = 'fluid' as const;

  public static FLUID_VERSION = 'v1' as const;

  public static FLUID_SEPARATOR = ':' as const;

  private parts: string[] = [];

  private constructor() {}

  public static withAccountId(
    accountId: string,
  ): FluidBuilder<`${typeof FluidBuilder.FLUID_PREFIX}:${typeof FluidBuilder.FLUID_VERSION}:account:${string}`> {
    const builder = new FluidBuilder();
    builder.parts = [FluidBuilder.FLUID_PREFIX, FluidBuilder.FLUID_VERSION, 'account', accountId];
    return builder as FluidBuilder<`${typeof FluidBuilder.FLUID_PREFIX}:${typeof FluidBuilder.FLUID_VERSION}:account:${string}`>;
  }

  public withEntity<E extends string>(entity: E, id: string): FluidBuilder<`${T}:${E}:${string}`> {
    this.parts.push(entity, id);
    return this as FluidBuilder<`${T}:${E}:${string}`>;
  }

  public toString(): T {
    return this.parts.join(FluidBuilder.FLUID_SEPARATOR) as T;
  }
}

export type FluidPrefixType = `${typeof FluidBuilder.FLUID_PREFIX}:${typeof FluidBuilder.FLUID_VERSION}:account:${string}`;

export class Fluid {
  public static fromString<T extends string>(fluid: T): { [key: string]: string; accountId: string } {
    const parts = fluid.split(FluidBuilder.FLUID_SEPARATOR);
    if (parts.length < 4) {
      throw new Error('Invalid fluid format');
    }

    const accountId = parts[3];
    const entity = parts.slice(4).reduce<{ [key: string]: string }>((acc, part, index) => {
      if (index % 2 === 0) {
        acc[part] = parts[index + 4 + 1]!;
      }

      return acc;
    }, {});

    if (!accountId) {
      throw new Error('Account ID is required');
    }

    return {
      ...entity,
      accountId,
    };
  }

  public static isValid(fluid: unknown, version: 'v1' = 'v1'): fluid is FluidPrefixType {
    if (typeof fluid !== 'string') {
      return false;
    }

    return (
      fluid.startsWith(`${FluidBuilder.FLUID_PREFIX}${FluidBuilder.FLUID_SEPARATOR}${version}${FluidBuilder.FLUID_SEPARATOR}`) &&
      fluid.split(FluidBuilder.FLUID_SEPARATOR).length > 3
    );
  }
}
